import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaArrowLeft, FaSpinner,FaShare, FaCamera } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import Breadcrumbs from '../components/Breadcrumbs';
import WebApplicationSchema from '../components/WebApplicationSchema';
import FAQPageSchema from '../components/FAQPageSchema';

const hairstyles = {
  men: [
    {
        id: "147641889812645907",
        name: "Crewcut Purple",
        image: "/hairstyles/men-hairstyle/Crewcut Purple.jpg",
        gender: "men",
        description: "Vibrant purple crewcut for a bold style",
        suitableFor: "Ideal for those who want a bold and edgy look, suitable for confident individuals who enjoy standing out.",
        maintenance: "Requires regular trims every 2-4 weeks and color touch-ups to maintain vibrancy."
    },
    {
        id: "3887",
        name: "Buzzcut",
        image: "/hairstyles/men-hairstyle/Buzzcut.jpg",
        gender: "men",
        description: "Classic buzzcut for a clean , neat and dashing",
        suitableFor: "Perfect for low-maintenance individuals, athletes, or those who prefer a neat and simple style.",
        maintenance: "Minimal maintenance, occasional trims every 2-3 weeks to maintain uniform length."
    },
    {
        id: "7571",
        name: "Bowlcut",
        image: "/hairstyles/men-hairstyle/Bowlcut.jpg",
        gender: "men",
        description: "Retro bowlcut for a youthful vibe",
        suitableFor: "Best for individuals who enjoy retro or alternative styles, works well for straight hair.",
        maintenance: "Requires trimming every 3-5 weeks to maintain the signature shape."
    },
    {
        id: "155157438734009345",
        name: "Long Wavy",
        image: "/hairstyles/men-hairstyle/Long Wavy.jpg",
        gender: "men",
        description: "Flowing, long wavy hair for a relaxed, natural style",
        suitableFor: "Great for those who prefer a laid-back, natural look. Works best for medium to thick wavy hair.",
        maintenance: "Regular conditioning, occasional trims to prevent split ends, and daily styling to manage waves."
    },
    {
        id: "147208380518237185",
        name: "Long Wavy Blond",
        image: "/hairstyles/men-hairstyle/Long Wavy Blond.jpg",
        gender: "men",
        description: "Blonde long wavy hair for a laid-back, beachy look",
        suitableFor: "Best for those who enjoy a surfer-inspired or relaxed beachy aesthetic.",
        maintenance: "Requires deep conditioning, regular trims, and occasional color touch-ups for maintaining the blonde shade."
    },
    {
        id: "5239",
        name: "Crewcut",
        image: "/hairstyles/men-hairstyle/Crewcut.jpg",
        gender: "men",
        description: "Short, stylish crewcut for a professional look",
        suitableFor: "Ideal for professionals, military personnel, and individuals looking for a sharp, low-maintenance style.",
        maintenance: "Requires trimming every 3-4 weeks to maintain a clean look."
    },
    {
        id: "7575",
        name: "Curtain",
        image: "/hairstyles/men-hairstyle/Curtain.jpg",
        gender: "men",
        description: "Curtain hairstyle with side-swept bangs for a trendy look",
        suitableFor: "Best for medium-length hair, suits oval and heart-shaped faces. Popular among fashion-forward individuals.",
        maintenance: "Requires regular shampooing, styling with a blow dryer or pomade, and trims every 6-8 weeks."
    },
    {
        id: "7577",
        name: "Short Curly",
        image: "/hairstyles/men-hairstyle/Short Curly.jpg",
        gender: "men",
        description: "Short and curly hair for a voluminous and playful style",
        suitableFor: "Great for individuals with naturally curly hair looking for a short yet defined style.",
        maintenance: "Requires moisturizing products to maintain curl definition and occasional trims every 4-6 weeks."
    },
    {
        id: "147643262310549526",
        name: "Short Curly Indigo",
        image: "/hairstyles/men-hairstyle/Short Curly Indigo.jpg",
        gender: "men",
        description: "Short curly hair with a bold indigo color for a unique look",
        suitableFor: "Perfect for individuals who love experimenting with vibrant hair colors and want a stylish curly look.",
        maintenance: "Requires color touch-ups every few weeks and curl-defining products for upkeep."
    },
    {
        id: "107400721166501891",
        name: "Bald",
        image: "/hairstyles/men-hairstyle/Bald.jpg",
        gender: "men",
        description: "Clean-shaven head for a minimalist, sleek style",
        suitableFor: "Ideal for those who prefer a no-maintenance look, individuals experiencing hair loss, or those who want a bold style.",
        maintenance: "Requires regular shaving or head grooming to maintain a smooth appearance."
    }
  ]
  ,
  women: [
      {
        id: "6659",
        name: "Messy Bun",
        image: "/hairstyles/women-hairstyle/Messy Bun.jpg",
        gender: "female",
        description: "Casual, messy bun for an effortless look",
        suitableFor: "Casual outings, busy mornings, gym, office, and laid-back events",
        maintenance: "Low; requires minimal styling and occasional hair spray for hold"
      },
      {
        id: "3885",
        name: "Long Wavy",
        image: "/hairstyles/women-hairstyle/Long Wavy.jpg",
        gender: "female",
        description: "Soft, long waves for a flowing, natural style",
        suitableFor: "Formal events, date nights, casual wear, and beach outings",
        maintenance: "Medium; requires regular conditioning and occasional curling to maintain waves"
      },
      {
        id: "6657",
        name: "Long Straight",
        image: "/hairstyles/women-hairstyle/Long Straight.jpg",
        gender: "female",
        description: "Sleek, straight long hair for a smooth and polished look",
        suitableFor: "Professional settings, formal events, and everyday wear",
        maintenance: "High; requires frequent straightening, heat protection, and regular trims"
      },
      {
        id: "6655",
        name: "Pixie",
        image: "/hairstyles/women-hairstyle/Pixie.jpg",
        gender: "female",
        description: "Chic pixie cut for a bold, fashionable style",
        suitableFor: "Bold personalities, low-maintenance lifestyles, and professional settings",
        maintenance: "Low to medium; requires regular trims to maintain shape"
      },
      {
        id: "107396801031570693",
        name: "Medium Straight",
        image: "/hairstyles/women-hairstyle/Medium Straight.jpg",
        gender: "female",
        description: "Medium-length straight hair for a versatile, everyday look",
        suitableFor: "Work, casual outings, and semi-formal events",
        maintenance: "Medium; requires straightening or smoothing products to keep sleek"
      },
      {
        id: "133036255729420334",
        name: "Tight Curls",
        image: "/hairstyles/women-hairstyle/Tight Curls.jpg",
        gender: "female",
        description: "Tight curls for a voluminous and textured hairstyle",
        suitableFor: "Casual and formal events, ideal for natural curls",
        maintenance: "High; requires hydration, curl creams, and regular trims"
      },
      {
        id: "148299176935949341",
        name: "Messy Bun Brown",
        image: "/hairstyles/women-hairstyle/Messy Bun Brown.jpg",
        gender: "female",
        description: "Messy bun with brown hair for a casual yet stylish look",
        suitableFor: "Everyday wear, casual meetups, gym, and office",
        maintenance: "Low; requires minimal styling effort and occasional hairpins"
      },
      {
        id: "142176244283213827",
        name: "Long Wavy Blonde",
        image: "/hairstyles/women-hairstyle/Long Wavy Blonde.jpg",
        gender: "female",
        description: "Blonde long wavy hair for a beachy, effortless style",
        suitableFor: "Beach outings, vacations, and casual wear",
        maintenance: "Medium to high; requires moisture treatments and occasional touch-ups for color"
      },
      {
        id: "133029014779462706",
        name: "Casual Updo",
        image: "/hairstyles/women-hairstyle/Casual Updo.jpg",
        gender: "female",
        description: "Casual updo for an easy, sophisticated look",
        suitableFor: "Formal and semi-formal events, office settings",
        maintenance: "Medium; requires occasional restyling and hairpins"
      },
      {
        id: "10819",
        name: "Beachy",
        image: "/hairstyles/women-hairstyle/Beachy.jpg",
        gender: "female",
        description: "Beachy waves for a laid-back, sun-kissed style",
        suitableFor: "Summer vacations, casual and bohemian styles",
        maintenance: "Medium; needs salt sprays and occasional re-curling"
      },
      {
        id: "155244553626978384",
        name: "Mid-Length Curls",
        image: "/hairstyles/women-hairstyle/Mid-Length Curls.jpg",
        gender: "female",
        description: "Mid-length curls for a bouncy, voluminous look",
        suitableFor: "Weddings, parties, and everyday chic looks",
        maintenance: "Medium to high; requires curl maintenance and hydration"
      },
      {
        id: "148023795112739897",
        name: "Long Straight Black",
        image: "/hairstyles/women-hairstyle/Long Straight Black.jpg",
        gender: "female",
        description: "Long straight black hair for a sleek and elegant style",
        suitableFor: "Formal occasions, business settings, and cultural events",
        maintenance: "High; requires regular straightening, deep conditioning, and frizz control"
      },    
        {
          id: "142177410215838726",
          name: "Pixie Pink",
          image: "/hairstyles/women-hairstyle/Pixie Pink.jpg",
          gender: "female",
          description: "A bold pink pixie cut for a vibrant, edgy look",
          suitableFor: "Those looking for a bold, low-maintenance, and modern haircut with a pop of color.",
          maintenance: "Requires frequent trims and color touch-ups to maintain the vibrant pink hue."
        },
        {
          id: "10857",
          name: "Bobcut Wavy",
          image: "/hairstyles/women-hairstyle/Bobcut Wavy.jpg",
          gender: "female",
          description: "Wavy bobcut for a stylish and chic appearance",
          suitableFor: "Ideal for those who want a versatile, easy-to-style, and trendy look.",
          maintenance: "Requires occasional trims to maintain shape and light styling to enhance waves."
        },
        {
          id: "5213",
          name: "Bobcut",
          image: "/hairstyles/women-hairstyle/Bobcut.jpg",
          gender: "female",
          description: "Classic bobcut for a clean and polished look",
          suitableFor: "Great for professionals and those who prefer a timeless, low-maintenance style.",
          maintenance: "Requires regular trims to maintain the sharp, structured shape."
        },
        {
          id: "148023795045631032",
          name: "Bobcut Green",
          image: "/hairstyles/women-hairstyle/Bobcut Green.jpg",
          gender: "female",
          description: "A vibrant green bobcut for a unique and bold style",
          suitableFor: "Best for those who love experimenting with colors and bold fashion statements.",
          maintenance: "Requires regular color touch-ups and trims to keep the cut sharp."
        },
        {
          id: "5217",
          name: "Curtain Wavy",
          image: "/hairstyles/women-hairstyle/Curtain Wavy.jpg",
          gender: "female",
          description: "Wavy curtain hairstyle for a soft and romantic look",
          suitableFor: "Perfect for those who want a stylish yet relaxed and feminine hairstyle.",
          maintenance: "Requires minimal styling, occasional trims, and light wave-enhancing products."
        },
        {
          id: "150095442237982805",
          name: "Bouncy Curls",
          image: "/hairstyles/women-hairstyle/Bouncy Curls.jpg",
          gender: "female",
          description: "Bouncy curls for a fun, voluminous style",
          suitableFor: "Best for those looking for a lively, full-bodied curly hairstyle.",
          maintenance: "Needs curl-enhancing products, moisture treatments, and occasional trims."
        },
        {
          id: "5211",
          name: "Afro",
          image: "/hairstyles/women-hairstyle/Afro.jpg",
          gender: "female",
          description: "Bold afro for a natural and striking look",
          suitableFor: "Great for those embracing their natural curls and volume.",
          maintenance: "Requires deep conditioning, moisture retention, and shape maintenance."
        },
        {
          id: "5215",
          name: "Romantic Waves",
          image: "/hairstyles/women-hairstyle/Romantic Waves.jpg",
          gender: "female",
          description: "Soft, romantic waves for a dreamy and elegant appearance",
          suitableFor: "Perfect for weddings, special events, or a glamorous daily look.",
          maintenance: "Requires curling tools, light styling, and occasional trims."
        },
        {
          id: "6663",
          name: "Loose Waves",
          image: "/hairstyles/women-hairstyle/Loose Waves.jpg",
          gender: "female",
          description: "Loose waves for a relaxed, carefree vibe",
          suitableFor: "Ideal for those looking for a natural yet stylish everyday look.",
          maintenance: "Needs light styling, occasional trims, and wave-enhancing products."
        },
        {
          id: "142180678971164676",
          name: "Loose Waves Reddish",
          image: "/hairstyles/women-hairstyle/Loose Waves Reddish.jpg",
          gender: "female",
          description: "Loose reddish waves for a warm, rich hairstyle",
          suitableFor: "Great for those who prefer a natural look with a hint of color.",
          maintenance: "Requires color care treatments and light wave maintenance."
        },
        {
          id: "155537250061387827",
          name: "Sleek Middle Part",
          image: "/hairstyles/women-hairstyle/Sleek Middle Part.jpg",
          gender: "female",
          description: "Sleek middle part for a sophisticated and polished look",
          suitableFor: "Perfect for a professional, modern, and polished appearance.",
          maintenance: "Needs regular straightening, frizz control, and trims."
        },
        {
          id: "6669",
          name: "Short Bob",
          image: "/hairstyles/women-hairstyle/Short Bob.jpg",
          gender: "female",
          description: "A short bob for a clean, modern look",
          suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
          maintenance: "Requires frequent trims to maintain shape and structure."
        },
        {
          id: "157312489262941356",
          name: "Short Bob Inky Blue",
          image: "/hairstyles/women-hairstyle/Short Bob Inky Blue.jpg",
          gender: "female",
          description: "A short bob with an inky blue shade for a striking appearance",
          suitableFor: "Ideal for those who enjoy experimenting with bold hair colors.",
          maintenance: "Requires regular color upkeep and trims to maintain vibrancy."
        },      
        {
          id: "116819137308657732",
          name: "Curly Bob",
          image: "/hairstyles/women-hairstyle/Curly Bob.jpg",
          gender: "female",
          description: "A curly bob for a playful and voluminous style",
          suitableFor: "Ideal for those with naturally curly or wavy hair who want a stylish yet manageable look.",
          maintenance: "Requires regular trimming and hydrating products to maintain curl definition."
        },
        {
          id: "169979129947489417",
          name: "Buzz Cut",
          image: "/hairstyles/women-hairstyle/Buzz Cut.jpg",
          gender: "female",
          description: "A buzz cut for a bold and minimalistic style",
          suitableFor: "Perfect for those who want an ultra-low maintenance and edgy look.",
          maintenance: "Minimal maintenance; occasional touch-ups to maintain even length."
        },
        {
          id: "10817",
          name: "Combover",
          image: "/hairstyles/women-hairstyle/Combover.jpg",
          gender: "female",
          description: "A sleek combover hairstyle for a refined look",
          suitableFor: "Great for those looking for a polished, professional appearance.",
          maintenance: "Requires styling products and occasional trims to keep the shape intact."
        },
        {
          id: "116731147513235462",
          name: "Gentle Waves",
          image: "/hairstyles/women-hairstyle/Gentle Waves.jpg",
          gender: "female",
          description: "Soft, gentle waves for a smooth, elegant appearance",
          suitableFor: "Perfect for those who want a sophisticated and effortless look.",
          maintenance: "Regular use of a curling wand or leave-in conditioners to maintain waves."
        },
        {
          id: "6665",
          name: "Bobcut Curly",
          image: "/hairstyles/women-hairstyle/Bobcut Curly.jpg",
          gender: "female",
          description: "Curly bobcut for a fun, bouncy hairstyle",
          suitableFor: "Great for naturally curly hair types or those who want to add volume.",
          maintenance: "Requires curl-enhancing products and regular trims to keep the shape."
        },
        {
          id: "150805552383920223",
          name: "New York Beehive",
          image: "/hairstyles/women-hairstyle/New York Beehive.jpg",
          gender: "female",
          description: "A dramatic beehive hairstyle, perfect for making a bold statement",
          suitableFor: "Best for those looking for a vintage or high-fashion look.",
          maintenance: "Requires strong-hold hairspray and careful styling to maintain height."
        },
        {
          id: "6673",
          name: "Top Bun",
          image: "/hairstyles/women-hairstyle/Top Bun.jpg",
          gender: "female",
          description: "A top bun hairstyle for a clean, practical look",
          suitableFor: "Ideal for those who need a quick, stylish updo for work or casual outings.",
          maintenance: "Minimal maintenance, occasional brushing and styling gel for a polished look."
        },
        {
          id: "6675",
          name: "Space Buns",
          image: "/hairstyles/women-hairstyle/Space Buns.jpg",
          gender: "female",
          description: "Playful space buns for a fun, quirky style",
          suitableFor: "Perfect for festivals, casual outings, or anyone wanting a youthful, trendy look.",
          maintenance: "Requires hair ties, styling gel, and occasional touch-ups to keep buns in place."
        },
        {
          id: "6661",
          name: "Two Braids",
          image: "/hairstyles/women-hairstyle/Two Bairds.jpg",
          gender: "female",
          description: "Two braids for a stylish and practical look",
          suitableFor: "Great for an active lifestyle or a bohemian aesthetic.",
          maintenance: "Minimal upkeep; occasional oiling to keep hair healthy and frizz-free."
        }        
  ]
  ,
  extensions:[
    {
      id: '206227835667350899',
      name: 'Short',
      image: '/hairstyles/hairstyle_extension/206227835667350899_Length 1.jpg',
      gender: 'extension',
      description: "Short length for females",
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '206227835667350900',
      name: 'Medium',
      image: '/hairstyles/hairstyle_extension/206227835667350900_Length 2 .jpg',
      gender: 'extension',
      description: "Medium length for females",
      suitableFor: "Ideal for those who want a comfortable yet stylish look.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '206227835667350901',
      name: 'Long',
      image: '/hairstyles/hairstyle_extension/206227835667350901_Length 3.jpg',
      gender: 'extension',
      description: "Long length for females",
      suitableFor: "Best for those who want a long, full-bodied hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    }
  ]
  ,
  bangsWomen:[
    {
      id: '200000000000000814',
      name: 'Thin Bangs',
      image: '/hairstyles/bangs-103/200000000000000814_Thin Bangs.jpg',
      gender: 'bangs-103',
      description: 'A sleek and modern hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '200000000000000955',
      name : 'Left Side Swept',
      image: '/hairstyles/bangs-103/200000000000000955_Left Side Swept.jpg',
      gender: 'bangs-103',
      description: 'A trendy and feminine hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '200000000000000956',
      name : 'Right Side Swept',
      image: '/hairstyles/bangs-103/200000000000000956_Right Side Swept.jpg',
      gender: 'bangs-103',
      description: 'A trendy and feminine hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id : '200000000000001027',
      name : 'Sleek Bangs',
      image: '/hairstyles/bangs-103/200000000000001027_Sleek Bangs.jpg',
      gender: 'bangs-103',
      description: 'A sleek and modern hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id : '200000000000001028',
      name : 'Wispy Bangs',
      image: '/hairstyles/bangs-103/200000000000001028_Wispy Bangs.jpg',
      gender: 'bangs-103',
      description: 'A wispy and modern hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id : '200000000000001029',
      name : 'Centered Bangs',
      image: '/hairstyles/bangs-103/200000000000001029_Wispy Bangs 2.jpg',
      gender: 'bangs-103',
      description: 'A wispy and modern hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id : '206227835667350983',
      name : 'Split Mini Fringe',
      image: '/hairstyles/bangs-103/206227835667350983_Split Mini Fringe.jpg',
      gender: 'bangs-103',
      description: 'A short and modern hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id : '206227835667350984',
      name : 'Split Bangs',
      image: '/hairstyles/bangs-103/206227835667350984_Split Bangs.jpg',
      gender: 'bangs-103',
      description: 'A short and modern hairstyle for women',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id : '206227835667350985',
      name : 'Mini Fringe',
      image: '/hairstyles/bangs-103/206227835667350985_Mini Fringe.jpg',
      gender: 'bangs-103',
      description: 'Mini fringe for a modern look',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '219691778809268788',
      name : 'Blunt Bangs',
      image: '/hairstyles/bangs-103/219691778809268788_Blunt Bangs.jpg',
      gender: 'bangs-103',
      description: 'Blunt bangs for a modern look',
      suitableFor: "Best for those who want a low-maintenance yet chic hairstyle.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    }
  ]
  ,
  bangsMen:[
    {
      id: '219691778809269123',
      name : 'Blunt Bangs',
      image: '/hairstyles/bangs-104/219691778809269123_Blunt Bangs.jpg',
      gender: 'bangs-104',
      description: 'Modern blunt bangs for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269125',
      name : 'Sleek Bangs',
      image: '/hairstyles/bangs-104/219691778809269125_Sleek Bangs.jpg',
      gender: 'bangs-104',
      description: 'Modern sleek bangs for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269126',
      name : 'Mini Fringe',
      image: '/hairstyles/bangs-104/219691778809269126_Mini Fringe.jpg',
      gender: 'bangs-104',
      description: 'Modern mini fringe for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269128',
      name : 'Split Mini Fringe',
      image: '/hairstyles/bangs-104/219691778809269128_Split Mini Fringe.jpg',
      gender: 'bangs-104',
      description: 'Modern split mini fringe for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269129',
      name : 'Split Bangs',
      image: '/hairstyles/bangs-104/219691778809269129_Split Bangs.jpg',
      gender: 'bangs-104',
      description: 'Modern split bangs for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269158',
      name : 'Wispy Bangs',
      image: '/hairstyles/bangs-104/219691778809269158_Wispy Bangs.jpg',
      gender: 'bangs-104',
      description: 'Modern wispy bangs for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269159',
      name : 'Wispy Bangs 2',
      image: '/hairstyles/bangs-104/219691778809269159_Wispy Bangs 2.jpg',
      gender: 'bangs-104',
      description: 'Modern long wispy bangs for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269161',
      name : 'Right Side Swept',
      image: '/hairstyles/bangs-104/219691778809269161_Right Side Swept.jpg',
      gender: 'bangs-104',
      description: 'Modern right side swept bangs for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269162',
      name : 'Thin Bangs',
      image: '/hairstyles/bangs-104/219691778809269162_Thin Bangs.jpg',
      gender: 'bangs-104',
      description: 'Modern thin bangs for a modern look',
      suitableFor: "Men with a modern and stylish look.",
      maintenance: "Easy to maintain and style."
    },
    {
      id: '219691778809269232',
      name : 'Left Side Swept',
      image: '/hairstyles/bangs-104/219691778809269232_Left Side Swept.jpg',
      gender: 'bangs-104',
      description: 'Modern left side swept bangs for a modern look'
    }
  ]
  ,
  volumes:[
    {
      id: '200000000000000975',
      name: 'Subtle',
      image: '/hairstyles/hairstyle-volume-117112796700739608/200000000000000975_Subtle.jpg',
      gender: 'volume',
      description: 'Modern subtle volume for a modern look',
      suitableFor: "Ideal for those who want a low-maintenance yet stylish look.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '200000000000000976',
      name: 'Natural',
      image: '/hairstyles/hairstyle-volume-117112796700739608/200000000000000976_Natural.jpg',
      gender: 'volume',
      description: 'Modern natural volume for a modern look',
      suitableFor: "Ideal for those who want a low-maintenance yet stylish look.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '200000000000000977',
      name: 'Medium',
      image: '/hairstyles/hairstyle-volume-117112796700739608/200000000000000977_Medium.jpg',
      gender: 'volume',
      description: 'Modern medium volume for a modern look',
      suitableFor: "Ideal for those who want a low-maintenance yet stylish look.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '200000000000000978',
      name: 'High',
      image: '/hairstyles/hairstyle-volume-117112796700739608/200000000000000978_High.jpg',
      gender: 'volume',
      description: 'Modern high volume for a modern look',
      suitableFor: "Ideal for those who want a low-maintenance yet stylish look.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    },
    {
      id: '200000000000000979',
      name: 'Maximized',
      image: '/hairstyles/hairstyle-volume-117112796700739608/200000000000000979_Maximized.jpg',
      gender: 'volume',
      description: 'Modern maximized volume for a modern look',
      suitableFor: "Ideal for those who want a low-maintenance yet stylish look.",
      maintenance: "Requires frequent trims to maintain shape and structure."
    }
  ]
};

const HairstyleBlog = () => {
  const { name } = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [hairstyle, setHairstyle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHairstyleDetails = async () => {
      try {
        setLoading(true);
        const allHairstyles = [...hairstyles.men, ...hairstyles.women, ...hairstyles.extensions, ...hairstyles.bangsMen, ...hairstyles.bangsWomen,...hairstyles.volumes];
        let foundHairstyle;
        
        if (location.state?.id) {
          // If we have an ID in the state, use it for precise matching
          foundHairstyle = allHairstyles.find(h => h.id === location.state.id);
        } else {
          // Fallback to finding by name (decoded from URL)
          const decodedName = decodeURIComponent(name);
          foundHairstyle = allHairstyles.find(h => h.name === decodedName);
        }
        
        if (!foundHairstyle) {
          throw new Error('Hairstyle not found');
        }
        
        setHairstyle(foundHairstyle);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHairstyleDetails();
  }, [name, location.state]);

  const handleTryOn = () => {
    if (isAuthenticated) {
      navigate('/try-on', { 
        state: { 
          hairstyleId: location.state?.id,
          styleImage: hairstyle?.image
        } 
      });
    } else {
      navigate('/login', { state: { from: `/hairstyle/${name}` } });
    }
  };

  const handleShare = async (hairstyle) => {
    const shareUrl = `${window.location.origin}/hairstyle/${name}`;
    const shareTitle = `Check out this ${hairstyle.name} hairstyle!`;
    const shareText = hairstyle.description;

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } else {
        // Fallback to custom share menu
        const shareOptions = [
          {
            name: 'Facebook',
            url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
          },
          {
            name: 'Twitter',
            url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
          },
          {
            name: 'WhatsApp',
            url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`,
          },
        ];

        // Create and show a custom share menu
        const menu = document.createElement('div');
        menu.style.position = 'fixed';
        menu.style.top = '50%';
        menu.style.left = '50%';
        menu.style.transform = 'translate(-50%, -50%)';
        menu.style.backgroundColor = 'white';
        menu.style.padding = '1rem';
        menu.style.borderRadius = '0.5rem';
        menu.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
        menu.style.zIndex = '1000';

        shareOptions.forEach(option => {
          const button = document.createElement('button');
          button.textContent = `Share on ${option.name}`;
          button.style.display = 'block';
          button.style.width = '100%';
          button.style.padding = '0.5rem 1rem';
          button.style.marginBottom = '0.5rem';
          button.style.border = 'none';
          button.style.borderRadius = '0.25rem';
          button.style.backgroundColor = '#f0f0f0';
          button.style.cursor = 'pointer';
          button.onclick = () => {
            window.open(option.url, '_blank');
            document.body.removeChild(menu);
          };
          menu.appendChild(button);
        });

        // Add copy link option
        const copyButton = document.createElement('button');
        copyButton.textContent = 'Copy Link';
        copyButton.style.display = 'block';
        copyButton.style.width = '100%';
        copyButton.style.padding = '0.5rem 1rem';
        copyButton.style.marginBottom = '0.5rem';
        copyButton.style.border = 'none';
        copyButton.style.borderRadius = '0.25rem';
        copyButton.style.backgroundColor = '#f0f0f0';
        copyButton.style.cursor = 'pointer';
        copyButton.onclick = async () => {
          try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Link copied to clipboard!');
          } catch (err) {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy link');
          }
          document.body.removeChild(menu);
        };
        menu.appendChild(copyButton);

        // Add close button
        const closeButton = document.createElement('button');
        closeButton.textContent = 'Close';
        closeButton.style.display = 'block';
        closeButton.style.width = '100%';
        closeButton.style.padding = '0.5rem 1rem';
        closeButton.style.border = 'none';
        closeButton.style.borderRadius = '0.25rem';
        closeButton.style.backgroundColor = '#ff4444';
        closeButton.style.color = 'white';
        closeButton.style.cursor = 'pointer';
        closeButton.onclick = () => document.body.removeChild(menu);
        menu.appendChild(closeButton);

        document.body.appendChild(menu);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <FaSpinner className="text-teal text-4xl animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="card text-center">
          <h2 className="text-2xl font-bold text-black mb-4">Oops!</h2>
          <p className="text-gray mb-6">{error}</p>
          <button
            onClick={() => navigate('/gallery')}
            className="btn btn-primary"
          >
            Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  if (!hairstyle) return null;

  const features = [
    {
      title: "Hairstyles Details",
      description: "Experience the magic of AI as it transforms your photos with stunning new hairstyles in seconds."
    },
    {
      title: "Realistic Hairstyle",
      description: "Our advanced AI ensures natural-looking results that match your facial features perfectly."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Hairstyle Studio Salon- Hairstyles",
    "description": "Get in-depth insights on different hairstyles with AI Hairstyle Studio! Explore detailed descriptions, styling tips, and virtual try-on options to find the perfect haircut and color for your look",
    "applicationCategory": "Beauty & Lifestyle",
    "featureList": features.map(f => f.title).join(", ")
  };

  return (<>
    <Helmet>
        <WebApplicationSchema />
        <Breadcrumbs />
        <FAQPageSchema />
        <title>AI Hairstyle Studio - Hairstyle Gallery</title>
        <meta name="description" content="Get in-depth insights on different hairstyles with AI Hairstyle Studio! Explore detailed descriptions, styling tips, and virtual try-on options to find the perfect haircut and color for your look" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
    <div className="min-h-screen bg-white">
      <div className="container py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="btn btn-secondary mb-6 flex items-center gap-2"
        >
          <FaArrowLeft /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="card overflow-hidden"
          >
            <img
              src={hairstyle.image}
              alt={hairstyle.name}
              loading="lazy"
              className="w-full h-[500px] object-cover rounded-xl"
            />
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="card">
              <h1 className="text-3xl font-bold text-black mb-4">{hairstyle.name}</h1>
              <p className="text-gray mb-6">{hairstyle.description}</p>
              
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => handleShare(hairstyle)}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <FaShare /> Share
                </button>
              </div>

              <button
                onClick={handleTryOn}
                className="btn btn-primary w-full hover-scale flex items-center justify-center gap-2"
              >
                <FaCamera /> Try This Style
              </button>
            </div>

            {/* Additional Information */}
            <div className="card">
              <h2 className="text-xl font-semibold text-black mb-4">Style Details</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-black">Best For</h3>
                  <p className="text-gray">{hairstyle.suitableFor}</p>
                </div>
                <div>
                  <h3 className="font-medium text-black">Maintenance</h3>
                  <p className="text-gray">{hairstyle.maintenance}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
              </>
  );
};

export default HairstyleBlog;
